import { auth } from '@/data/firebase/config';
import { useAuth } from '@/domain/context/auth';
import { signOut } from 'firebase/auth';
import React, { useEffect, useRef, useState } from 'react';
import { MdMenu } from 'react-icons/md';
import { Link, useLocation } from 'react-router-dom';
import { buildBreadcrumb } from './breadcrumb-utils';
import { useSidebar } from './sidebar-context';

/**
 * Derives initials from a first name and last name.
 * - If both are provided, returns first char of each uppercased.
 * - If only first name is provided, returns first two chars uppercased.
 * - Returns '?' if nothing usable.
 */
export function getInitials(
  firstName: string | undefined,
  lastName: string | undefined,
): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  if (!first && !last) return '?';
  if (first && last) return (first[0] + last[0]).toUpperCase();
  // single word
  const word = first || last;
  return word.slice(0, 2).toUpperCase() || '?';
}

const AppHeader: React.FC = () => {
  const { openMobile } = useSidebar();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const breadcrumb = buildBreadcrumb(pathname);
  const initials = getInitials(user?.name, user?.lastname);

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch {
      // silent — firebase will redirect anyway
    }
  };

  // Close on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [dropdownOpen]);

  // Close on Escape
  useEffect(() => {
    if (!dropdownOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [dropdownOpen]);

  return (
    <header className="sticky top-0 z-10 h-16 bg-white border-b border-gray-200 flex items-center px-4 gap-4 flex-shrink-0">
      {/* Hamburger — mobile only */}
      <button
        onClick={openMobile}
        className="md:hidden p-2 rounded-md text-gray-500 hover:bg-gray-100 transition-colors"
        aria-label="Abrir menú"
      >
        <MdMenu size={22} aria-hidden="true" />
      </button>

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex-1 min-w-0">
        <ol className="flex items-center gap-1 text-sm">
          {breadcrumb.map((item, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && (
                <li className="text-gray-400 select-none" aria-hidden="true">
                  /
                </li>
              )}
              <li
                className={
                  item.path
                    ? 'text-gray-500 hover:text-gray-700'
                    : 'text-gray-900 font-medium truncate'
                }
              >
                {item.path ? (
                  <Link to={item.path} className="hover:underline">
                    {item.label}
                  </Link>
                ) : (
                  <span>{item.label}</span>
                )}
              </li>
            </React.Fragment>
          ))}
        </ol>
      </nav>

      {/* Avatar dropdown */}
      <div className="relative flex-shrink-0" ref={dropdownRef}>
        <button
          onClick={() => setDropdownOpen((prev) => !prev)}
          className="w-9 h-9 rounded-full bg-primary text-white text-sm font-semibold flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
          aria-label="Perfil de usuario"
          aria-expanded={dropdownOpen}
          aria-haspopup="true"
        >
          {initials}
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg border border-gray-100 z-20 py-1">
            <div className="px-4 py-3 border-b border-gray-100">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user ? `${user.name} ${user.lastname}` : ''}
              </p>
              <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
            </div>
            <div className="py-1">
              <button
                onClick={handleLogout}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                aria-label="Cerrar sesión"
              >
                Cerrar sesión
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
